import React, { useState, ChangeEvent, useEffect } from 'react';
import SubHeader from '../../../components/subheader';
import LoadingScreen from '../../../components/loading-screen';
import { ROUTES } from '../../../config/routes';
import { notificationActions } from '../../../store/reducers/notificationSlice';
import { importData } from '../../../store/actions/importDataAction';
import { importDataActions } from '../../../store/reducers/importDataSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Link,
    IStackStyles
} from '@fluentui/react';
import { CustomModal } from '../../../components/custom-modal';
import { DATA_IMPORT_TYPES, DATA_IMPORT_HREF } from './common';
import { getDocumentTitle } from "../../../utils/common";
import { convertBatchToImageColumn } from '../../../utils/excelImageColumn';

const customStackStyles: Partial<IStackStyles> = {
    root: {
        flex: "1 1 calc(25% - 10px)",
        margin: "15px",
    },
};

type Props = {
    route: string;
};

type WorkbookAndImages = [File, File[]];

const Import: React.FC<Props> = (props) => {
    const { route } = props;

    const title =
        route.includes("Lab")
            ? DATA_IMPORT_TYPES.labInventory
            : route.includes("Rack")
                ? DATA_IMPORT_TYPES.rack
                : DATA_IMPORT_TYPES.switch;

    const href =
        title === DATA_IMPORT_TYPES.labInventory
            ? DATA_IMPORT_HREF.labInventory
            : title === DATA_IMPORT_TYPES.switch
                ? DATA_IMPORT_HREF.switch
                : DATA_IMPORT_HREF.rack;

    const isRack = title === DATA_IMPORT_TYPES.rack;
    const isSwitch = title === DATA_IMPORT_TYPES.switch;
    const [files, setFiles] = useState<WorkbookAndImages | null>(null);
    const [key, setKey] = useState<string>('');
    const [status, setStatus] = useState('');

    const dispatch = useAppDispatch();

    const showLoading = useAppSelector(
        (state) => state.importData.showLoading
    );

    const notificationDetails = useAppSelector(
        (state) => state.notification.notificationDetails
    );

    const ACCEPTED_IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp']);

    const isExcelXlsx = (f: File) =>
        f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        f.name.toLowerCase().endsWith('.xlsx');

    const isImage = (f: File) => {
        const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
        return f.type.startsWith('image/') || ACCEPTED_IMAGE_EXTS.has(ext);
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const list = e.target.files;
        if (!list || list.length === 0) {
            setFiles(null);
            return;
        }

        const all = Array.from(list);
        const workbookFile = all.find(isExcelXlsx) ?? null;
        const imageFiles = all.filter(isImage);

        if (isRack) {
            if (!workbookFile || all.length !== 1) {
                alert('Please select only one Excel (.xlsx) file for Rack import.');
                setFiles(null);
                return;
            }
            setFiles([workbookFile, []]);
            return;
        }
        if (isSwitch) {
            if (!workbookFile || all.length !== 1) {
                alert('Please select only one Excel (.xlsx) file for Switch import.');
                setFiles(null);
                return;
            }
            setFiles([workbookFile, []]);
            return;
        }
        if (!workbookFile || imageFiles.length === 0) {
            alert('Please select one Excel (.xlsx) file and at least one image.');
            setFiles(null);
            return;
        }

        setFiles([workbookFile, imageFiles]);
    };

    const onSubmitHandler = async () => {
        if (!files) {
            return;
        }

        close();

        dispatch(
            importDataActions.setLoading({
                isLoading: true,
                isSuccess: false,
                sourceAction: title,
                state: true,
            })
        );

        try {
            let uploadFiles: File[];
            if (isRack || isSwitch) {
                uploadFiles = [files[0]];
            }
            else {
                setStatus('Converting images to Base64...');
                uploadFiles = await convertBatchToImageColumn(files, {
                    pictureHeader: 'picture',
                    newHeader: 'Image',
                });
            }

            setStatus('Uploading...');
            dispatch<any>(importData(uploadFiles, title));
            setStatus('Done.');
        } catch (err: any) {
            setStatus(err?.message ?? 'Failed to convert or upload');
            dispatch(
                notificationActions.showNotification({
                    isVisible: true,
                    notificationDetails: [
                        {
                            type: err?.error ?? "error",
                            message: err?.message ?? "Failed to convert or upload",
                            sourcePageURL: ROUTES["importLabInventoryInfo"].url,
                        },
                    ],
                })
            );
            setTimeout(() => {
                dispatch(notificationActions.hideNotification());
            }, 10000);
        }
    };

    const close = () => {
        dispatch(notificationActions.hideNotification());
        setFiles(null);
        setKey(prev => prev + 'i');
    };
    useEffect(() => {
        document.title = getDocumentTitle('Import ' + title);
    }, [title]);
    return (
        <>
            <SubHeader title={'Import ' + title} />
            {showLoading.state && <LoadingScreen />}
            <Stack styles={customStackStyles}>
                <Stack.Item align="auto">
                    <label>Data must be uploaded in the following format: </label>
                    <Link
                        href={href}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="import-sample-link">
                        {title} Import Sample.
                    </Link>
                </Stack.Item>
                <br />
                <Stack.Item align="auto">
                    <input
                        type="file"
                        multiple={!(isRack || isSwitch)}
                        accept={
                            (isRack || isSwitch)
                                ? '.xlsx'
                                : '.xlsx,image/*,.png,.jpg,.jpeg,.gif,.webp,.bmp'
                        }
                        onChange={handleFileChange}
                        id="fileUpload"
                        key={key}
                    />
                </Stack.Item>

                <Stack.Item align="end">
                    {showLoading.state && (
                        <Spinner
                            size={SpinnerSize.xSmall}
                            style={{ paddingLeft: 10 }}
                        />
                    )}
                    <PrimaryButton
                        color="primary"
                        onClick={onSubmitHandler}
                        disabled={
                            showLoading.sourceAction === ROUTES[route].title &&
                            showLoading.state
                        }>
                        Upload
                    </PrimaryButton>
                </Stack.Item>
            </Stack>

            {notificationDetails && notificationDetails.length > 0 && (
                <CustomModal
                    isModalOpen={notificationDetails.length > 0}
                    onDismiss={close}
                    title={
                        <div>
                            {notificationDetails[0].type?.toString() === "error" ? (
                                <div style={{ textAlign: 'center', color: 'red' }}>
                                    Import Failed
                                </div>
                            ) : notificationDetails[0].type?.toString() === "success" ? (
                                <div style={{ textAlign: 'center', color: '#52b202' }}>
                                    Import Successful
                                </div>
                            ) : (
                                "Import Status"
                            )}
                        </div>
                    }
                    content={<div>{notificationDetails[0].message}</div>}
                />
            )}
        </>
    );
};

export default Import;
